import { cn } from '@/lib/utils';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { UNDERWRITING_STATUS_CONFIG, type UnderwritingStatus } from '@shared';

interface UnderwritingStatusBadgeProps {
  status: UnderwritingStatus;
  className?: string;
  onViewDetails?: () => void;
  size?: 'sm' | 'md'; // sm for compact badges (like in FundingTable), md for quote cards (default)
}

export function UnderwritingStatusBadge({
  status,
  className,
  onViewDetails,
  size = 'md',
}: UnderwritingStatusBadgeProps) {
  const { flags } = useFeatureFlags();

  if (flags.nycLifeDemo) return null;

  if (!status) return null;

  const config = UNDERWRITING_STATUS_CONFIG[status];
  if (!config) return null;

  const isInteractive = !!onViewDetails;

  // Size-specific classes
  const sizeClasses =
    size === 'sm'
      ? 'w-max py-0.5' // Compact size to match "Recently Added" badge
      : 'w-28 py-1'; // Default size for quote cards

  if (isInteractive) {
    return (
      <button
        type="button"
        onClick={onViewDetails}
        data-testid="quote-detail-eligibility-status"
        className={cn(
          'inline-flex items-center justify-center rounded-full border px-2 text-xs font-medium transition-all duration-200',
          'focus:ring-primary hover:opacity-80 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1',
          'cursor-pointer',
          sizeClasses,
          config.classes.bgColor,
          config.classes.textColor,
          config.classes.borderColor,
          className,
        )}
        aria-label={`View ${config.label} details`}
      >
        {config.label}
      </button>
    );
  }

  return (
    <span
      data-testid="quote-detail-eligibility-status"
      className={cn(
        'inline-flex items-center justify-center rounded-full border px-2 text-xs font-medium',
        sizeClasses,
        config.classes.bgColor,
        config.classes.textColor,
        config.classes.borderColor,
        className,
      )}
    >
      {config.label}
    </span>
  );
}
